/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : SelectAPMFile.tsx
 *
 * Project Title : APM Settings Tool
 *
 * Description   : Select APM File component
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422337
 *
 * @Date         : Jan 04, 2022
 *
 * @Version      : Version 1.0
 *
 *
 ******************************************************************************/

import React, { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IOption } from "@scuf/common/dist/components/Select/ISelectProps";
import { Card, Grid, Button, Select, Checkbox } from "@scuf/common";
import {
  setCurrentWorkFlowStep,
  selectCurrentApmFileRecord,
  selectCurrentWorkFlowType,
} from "stores/ApmWorkFlowStore";
import {
  fetchAPMFile,
  selectApmFilesLoader,
  selectApmFileNames,
  setSelectedApmFile,
  selectSelectedFileName,
  selectshowPostScriptOnly,
  setSelectedPostScriptValue,
} from "stores/SelectApmFileStore";
import { selectCustomer } from "stores/CustomerStore";
import { setSettingsResetWarning } from "stores/UIStore";
import { ISelectApmFile } from "models/selectApmFile";
import "./SelectAPMFile.scss";
import {
  EnumApmSettingsWorkFlow,
  EnumNavigateAction,
} from "models/configuration";
import { CommonLoader } from "components/CommonLoader";

export const SelectAPMFile: FC = () => {
  const dispatch = useDispatch();
  const apmFileApiLoader = useSelector(selectApmFilesLoader);
  const selectedCutomer = useSelector(selectCustomer);
  const selectedFileName = useSelector(selectSelectedFileName);
  const currentApmFileRecord = useSelector(selectCurrentApmFileRecord);
  const currentWorkFlowType = useSelector(selectCurrentWorkFlowType);
  const showPostScriptOnly = useSelector(selectshowPostScriptOnly);

  let apmFileOptions: Array<IOption> = [];
  const apmFileNames: ISelectApmFile[] | null = useSelector(selectApmFileNames);

  useEffect(() => {
    dispatch(fetchAPMFile());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCutomer?.customerId]);

  if (apmFileNames) {
    let filteredFileNames: ISelectApmFile[] = [];
    if (showPostScriptOnly) {
      filteredFileNames = apmFileNames.filter((filename) =>
        filename.partNumber.includes("_ASF")
      );
    } else {
      filteredFileNames = apmFileNames;
    }
    apmFileOptions = filteredFileNames.map((apmFileName: ISelectApmFile) => {
      return {
        text: apmFileName?.partNumber,
        value: apmFileName?.partNumber,
      };
    });
  }
  apmFileOptions = [{ text: "Select", value: null }, ...apmFileOptions];

  const onChangeShowPostScriptOnly = (value: boolean) => {
    dispatch(setSelectedPostScriptValue(value));
    if (!selectedFileName?.partNumber.includes("_ASF") && value) {
      dispatch(setSelectedApmFile(null));
    }
  };

  const onClickBackButton = () => {
    dispatch(setCurrentWorkFlowStep(1));
  };
  const onClickSkipButton = () => {
    if (
      currentWorkFlowType === EnumApmSettingsWorkFlow.Edit &&
      currentApmFileRecord?.baseFileName
    ) {
      dispatch(
        setSettingsResetWarning({
          showSettingsResetWarningModal: true,
          navigateActionData: null,
          navigateAction: EnumNavigateAction.skip,
        })
      );
    } else {
      dispatch(setSelectedApmFile(null));
      dispatch(setCurrentWorkFlowStep(3));
    }
  };

  const onClickNextButton = () => {
    if (
      currentWorkFlowType === EnumApmSettingsWorkFlow.Edit &&
      currentApmFileRecord?.baseFileName !== selectedFileName?.partNumber &&
      !(
        currentApmFileRecord?.baseFileName === null && selectedFileName === null
      )
    ) {
      dispatch(
        setSettingsResetWarning({
          showSettingsResetWarningModal: true,
          navigateActionData: null,
          navigateAction: EnumNavigateAction.next,
        })
      );
    } else {
      dispatch(setCurrentWorkFlowStep(3));
    }
  };

  const onApmFileSelect = (value: string | null) => {
    if (value !== null) {
      const selectedApmFile = apmFileNames?.find(
        (apmFileName) => apmFileName.partNumber === value
      );

      if (selectedApmFile === undefined) {
        dispatch(setSelectedApmFile(null));
      } else {
        dispatch(setSelectedApmFile(selectedApmFile));
      }
    } else {
      dispatch(setSelectedApmFile(null));
    }
  };

  return (
    <div className="select-apm-file-wrapper">
      {apmFileApiLoader === true ? (
        <CommonLoader />
      ) : (
        <>
          <Card>
            <Card.Header>
              <h4> Select the existing APM File</h4>
            </Card.Header>
            <Card.Content>
              <Grid>
                <Grid.Row>
                  <Grid.Column lWidth={4} xsWidth={12}>
                    <Select
                      label="APM File Name"
                      placeholder="Select APM File"
                      options={apmFileOptions}
                      value={
                        selectedFileName ? selectedFileName?.partNumber : null
                      }
                      onChange={(value) => onApmFileSelect(value)}
                    />
                  </Grid.Column>
                  <Grid.Column
                    lWidth={4}
                    xsWidth={12}
                    className="postscript-checkbox"
                  >
                    <Checkbox
                      label="Show only files with PostScript"
                      defaultChecked={true}
                      checked={showPostScriptOnly}
                      onChange={(value) => onChangeShowPostScriptOnly(value)}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Card.Content>
          </Card>
          <Grid className="bottom-button-grid">
            <Grid.Row>
              <Grid.Column lWidth={3} xsWidth={12}>
                <Button
                  type="secondary"
                  size="medium"
                  content="Back"
                  onClick={onClickBackButton}
                />
              </Grid.Column>
              <Grid.Column lWidth={9} xsWidth={12}>
                <Grid.Row className="next-action-buttons">
                  <Button
                    type="secondary"
                    size="medium"
                    content="Skip & Continue with default APM Settings"
                    onClick={onClickSkipButton}
                  />

                  <Button
                    actionType="submit"
                    type="primary"
                    size="medium"
                    content="Next"
                    onClick={onClickNextButton}
                  />
                </Grid.Row>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </>
      )}
    </div>
  );
};
